// ===========================================
// MobileNav — Drawer mobile responsive (étape 3)
// Consomme navConfig (même source que la topnav desktop).
// Présentation : accordéon avec tuiles + actions rapides.
// ===========================================
import React, { useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { CloseOutlined, RightOutlined, PlusOutlined, LogoutOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';

import { AuthContext } from '../../../common/constants';
import { hasPerm, hasAny } from '../../../common/auth/permissions';
import { useT } from '../../../common/i18n';
import { getNavConfig } from '../navConfig';
import logo from '../../../assets/logo/logo-cap-assurances.JPG';

const canSee = (currentUser, item) => {
    if (!item) return false;
    if (item.perm) return hasPerm(currentUser, item.perm);
    if (item.anyOf) return hasAny(currentUser, item.anyOf);
    return true;
};

const filterSections = (currentUser, sections) =>
    (sections || [])
        .map((s) => ({ ...s, items: (s.items || []).filter((it) => canSee(currentUser, it)) }))
        .filter((s) => s.items.length > 0);

const isModuleActive = (pathname, matches) =>
    (matches || []).some((p) => pathname.startsWith(p));

// ─── Tuile dans le drawer (compacte, full width) ──────────────────────────
const DrawerTile = ({ item, onNavigate, isCurrent }) => (
    <button
        type="button"
        role="menuitem"
        className={`drawer-tile ${isCurrent ? 'is-current' : ''}`}
        onClick={() => onNavigate(item.path)}
        aria-current={isCurrent ? 'page' : undefined}
    >
        <div className="drawer-tile-icon">{item.icon}</div>
        <div className="drawer-tile-body">
            <div className="drawer-tile-label">{item.label}</div>
            {item.description && <div className="drawer-tile-desc">{item.description}</div>}
        </div>
        <RightOutlined className="drawer-tile-chevron" />
    </button>
);

// ─── Section accordéon (titre cliquable + items) ──────────────────────────
const AccordionSection = ({ megamenu, currentPath, isOpen, onToggle, onNavigate, currentUser }) => {
    const filteredSections = filterSections(currentUser, megamenu.sections);
    if (filteredSections.length === 0) return null;

    const isActive = isModuleActive(currentPath, megamenu.matches);

    return (
        <div className={`drawer-section ${isOpen ? 'is-open' : ''}`}>
            <button
                type="button"
                className={`drawer-section-header ${isActive ? 'is-active' : ''}`}
                onClick={onToggle}
                aria-expanded={isOpen}
                aria-controls={`drawer-panel-${megamenu.key}`}
            >
                <div className="drawer-section-title">
                    <span className="drawer-section-icon">{megamenu.icon}</span>
                    <span>{megamenu.label}</span>
                </div>
                <RightOutlined className={`drawer-section-chevron ${isOpen ? 'is-rotated' : ''}`} />
            </button>

            {isOpen && (
                <div className="drawer-section-body" id={`drawer-panel-${megamenu.key}`}>
                    {filteredSections.map((section, sIdx) => {
                        const isActions = section.type === 'actions';
                        return (
                            <div
                                key={section.title || sIdx}
                                className={`drawer-sub-section ${isActions ? 'is-actions' : ''}`}
                            >
                                {section.title && (
                                    <div className="drawer-sub-title">{section.title}</div>
                                )}
                                {isActions ? (
                                    <div className="drawer-actions">
                                        {section.items.map((item) => (
                                            <button
                                                key={item.key}
                                                type="button"
                                                className="drawer-action-btn"
                                                onClick={() => onNavigate(item.path)}
                                            >
                                                <PlusOutlined />
                                                <span>{item.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="drawer-tiles">
                                        {section.items.map((item) => (
                                            <DrawerTile
                                                key={item.key}
                                                item={item}
                                                onNavigate={onNavigate}
                                                isCurrent={item.path && currentPath.startsWith(item.path)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─── Composant principal ──────────────────────────────────────────────────
const MobileNav = ({ onClose }) => {
    const t = useT();
    const auth = useContext(AuthContext);
    const { currentUser } = auth;
    const location = useLocation();
    const navigate = useNavigate();

    const { dashboard, megamenus } = getNavConfig(t);

    // Ouvre par défaut le mégamenu correspondant à la page actuelle
    const activeKey = megamenus.find((mm) => isModuleActive(location.pathname, mm.matches))?.key;
    const [openKey, setOpenKey] = useState(activeKey || null);

    // Esc ferme le drawer
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    const handleNavigate = (path) => {
        if (path) navigate(path);
        onClose?.();
    };

    const handleLogout = () => {
        toast.success(t('auth.logout.success'), { autoClose: 1500 });
        setTimeout(() => auth.onLogout(), 600);
        onClose?.();
    };

    const dashboardVisible = !dashboard.perm || hasPerm(currentUser, dashboard.perm);
    const isDashboardActive = location.pathname === dashboard.path || location.pathname === `${dashboard.path}/`;

    return (
        <aside className="mobile-nav-drawer" role="dialog" aria-label={t('nav.open_menu')}>
            {/* Header : logo + close */}
            <div className="drawer-header">
                <Link to="/dashboard" className="drawer-brand" onClick={onClose}>
                    <img src={logo} alt="CAP Assurances" />
                </Link>
                <button
                    type="button"
                    className="drawer-close"
                    onClick={onClose}
                    aria-label={t('nav.close_menu')}
                >
                    <CloseOutlined />
                </button>
            </div>

            {/* Body : Dashboard direct + accordéons */}
            <nav className="drawer-body" role="menu">
                {dashboardVisible && (
                    <button
                        type="button"
                        role="menuitem"
                        className={`drawer-direct-link ${isDashboardActive ? 'is-active' : ''}`}
                        onClick={() => handleNavigate(dashboard.path)}
                        aria-current={isDashboardActive ? 'page' : undefined}
                    >
                        <span className="drawer-section-icon">{dashboard.icon}</span>
                        <span>{dashboard.label}</span>
                    </button>
                )}

                {megamenus.map((mm) => (
                    <AccordionSection
                        key={mm.key}
                        megamenu={mm}
                        currentPath={location.pathname}
                        currentUser={currentUser}
                        isOpen={openKey === mm.key}
                        onToggle={() => setOpenKey((prev) => (prev === mm.key ? null : mm.key))}
                        onNavigate={handleNavigate}
                    />
                ))}
            </nav>

            {/* Footer : profil + logout */}
            <div className="drawer-footer">
                <button
                    type="button"
                    className="drawer-footer-btn"
                    onClick={() => handleNavigate('/dashboard/profile')}
                >
                    {t('nav.my_profile')}
                </button>
                <button
                    type="button"
                    className="drawer-footer-btn drawer-footer-btn-danger"
                    onClick={handleLogout}
                >
                    <LogoutOutlined /> <span>{t('nav.logout')}</span>
                </button>
            </div>
        </aside>
    );
};

export default MobileNav;
